/**
 * OpenStreetMap's power lines, fences, walls, hedges and masts, built on the
 * ground of a tile: the meshes, and the walls the player and the car stop at.
 *
 * - **Power lines:** a `line` on steel lattice pylons (drawn as a tapered
 *   tower with a cross-arm) about 22 m tall, three conductors sagging between
 *   them; a `minor_line` on wooden poles 9 m tall, two conductors. The pylons
 *   stand at the way's nodes, which is where OSM puts them.
 * - **Fences:** posts every 2.5 m and two rails; **walls:** a stone wall a
 *   metre high (or its `height`); **hedges:** a green box 1.5 m tall.
 * - **Masts and cell towers:** a lattice mast 40 m tall (or its `height`)
 *   with antennas near the top, red and white bands as masts are painted.
 *
 * Lines arrive whole from every tile they touch; each tile builds the part
 * inside it: pylons whose node is in the tile, the wires and barriers clipped
 * to it. So nothing is built twice.
 */

/** The kinds, in the order `OsmLineData.kinds` indexes them. */
export const OSM_LINE_KINDS = ['power', 'minor_power', 'fence', 'wall', 'hedge'] as const;

export interface OsmLineData {
  /** x, z pairs in chunk metres, every line's in turn. */
  points: Float32Array;
  /** First point and point count per line. */
  ranges: Uint32Array;
  /** Index into OSM_LINE_KINDS per line. */
  kinds: Uint8Array;
  /** `height` per line, NaN when untagged. */
  heights: Float32Array;
  /** Per line: `LINE_RAISED` on a bridge's deck, `LINE_METAL` a metal railing. Absent, neither. */
  flags?: Uint8Array;
}

export const LINE_RAISED = 1;
export const LINE_METAL = 2;

export interface OsmLineMeshes {
  mesh: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** x0, z0, x1, z1, bottom, top per piece, as `ColliderInput.walls`. */
  walls: Float32Array;
}

const STEEL = [0.46, 0.48, 0.5];
const WIRE = [0.16, 0.16, 0.17];
const WOOD = [0.33, 0.24, 0.16];
const FENCE = [0.42, 0.33, 0.24];
const STONE = [0.5, 0.49, 0.46];
const HEDGE = [0.13, 0.26, 0.1];
const RED = [0.62, 0.08, 0.06];
const WHITE = [0.82, 0.82, 0.8];

/** A pylon's height, its cross-arm's half-width, and the heights of its conductors. */
const PYLON = { height: 22, arm: 5, base: 2.2, top: 0.6 };
const POLE = { height: 9, arm: 0.9, radius: 0.13 };
/** How far a span sags at its middle, as a share of its length. */
const SAG = 0.025;
const FENCE_POST = 2.5;
const METAL = [0.56, 0.58, 0.6];
/** A metal railing's balusters, metres apart. */
const BALUSTER = 0.6;

/** How far a way must turn across a barrier to pass through it, as the cosine of the angle off it: 30 degrees. */
const ACROSS = Math.cos(Math.PI / 6);
/** The steps a barrier is tested for ways through it in, metres. */
const GAP_STEP = 0.5;
const GAP_CELL = 16;

/** How far above or below a barrier's foot a way can be and still pass through it, metres: not a road under a bridge. */
const THROUGH = 2.5;

/**
 * Where the roads and paths pass through a barrier mapped across them. A
 * fence along a quay is often mapped straight past the footbridges off it,
 * and stood across every one: a way crossing a barrier at more than 30
 * degrees passes through it. A barrier along a way stays, and so does one
 * the way only passes over or under: a footbridge's railings over the
 * motorway below.
 */
export class WayCrossings {
  /** Per ribbon quad: its corners (x, z four times), its direction along, and its lowest and highest point. */
  private readonly quads: number[] = [];
  private readonly cells = new Map<number, number[]>();

  /**
   * `positions`: the road ribbons, flat x, z pairs, a left and a right edge
   * per cross-section; quads six indices each. `placed`, where given: the
   * same vertices as laid (x, y, z), for their heights.
   */
  constructor(positions: Float32Array, indices: Uint32Array, placed?: Float32Array) {
    for (let q = 0; q < Math.floor(indices.length / 6); q++) {
      const a = indices[q * 6];
      const corners = [a, a + 1, a + 3, a + 2];
      const xs = corners.map(v => positions[v * 2]), zs = corners.map(v => positions[v * 2 + 1]);
      const ux = (xs[2] + xs[3] - xs[0] - xs[1]) / 2, uz = (zs[2] + zs[3] - zs[0] - zs[1]) / 2, len = Math.hypot(ux, uz);
      if (len < 1e-6) continue;
      const ys = placed === undefined ? [-Infinity, Infinity] : corners.map(v => placed[v * 3 + 1]);
      const i = this.quads.length / 12;
      this.quads.push(xs[0], zs[0], xs[1], zs[1], xs[2], zs[2], xs[3], zs[3], ux / len, uz / len, Math.min(...ys), Math.max(...ys));
      for (let cx = Math.floor(Math.min(...xs) / GAP_CELL); cx <= Math.floor(Math.max(...xs) / GAP_CELL); cx++) {
        for (let cz = Math.floor(Math.min(...zs) / GAP_CELL); cz <= Math.floor(Math.max(...zs) / GAP_CELL); cz++) {
          const k = cx * 73856093 ^ cz * 19349663;
          const list = this.cells.get(k);
          if (list === undefined) this.cells.set(k, [i]); else list.push(i);
        }
      }
    }
  }

  /**
   * Whether a way runs through (x, z) across a barrier running along (dx,
   * dz), a unit vector, its foot at `y` where known.
   */
  crosses(x: number, z: number, dx: number, dz: number, y?: number): boolean {
    const list = this.cells.get(Math.floor(x / GAP_CELL) * 73856093 ^ Math.floor(z / GAP_CELL) * 19349663);
    if (list === undefined) return false;
    const Q = this.quads;
    for (const i of list) {
      const o = i * 12;
      if (Math.abs(Q[o + 8] * dx + Q[o + 9] * dz) > ACROSS) continue;
      if (y !== undefined && (y < Q[o + 10] - THROUGH || y > Q[o + 11] + THROUGH)) continue;
      // Inside the quad, its corners in order round it.
      let sign = 0, inside = true;
      for (let k = 0; k < 4 && inside; k++) {
        const ax = Q[o + k * 2], az = Q[o + k * 2 + 1], bx = Q[o + ((k + 1) % 4) * 2], bz = Q[o + ((k + 1) % 4) * 2 + 1];
        const c = (bx - ax) * (z - az) - (bz - az) * (x - ax);
        if (Math.abs(c) < 1e-9) continue;
        if (sign === 0) sign = Math.sign(c); else if (Math.sign(c) !== sign) inside = false;
      }
      if (inside) return true;
    }
    return false;
  }
}

/**
 * `deck`, where given: the bridge deck a raised barrier (`LINE_RAISED`) stands
 * on at (x, z), or undefined where there is none and it stands on the ground.
 * `ways`, where given: where the roads and paths pass through the barriers.
 */
export function buildOsmLines(
  data: OsmLineData | undefined, masts: Float32Array | undefined, width: number, depth: number,
  ground: (x: number, z: number) => number,
  deck?: (x: number, z: number) => number | undefined,
  ways?: WayCrossings,
): OsmLineMeshes {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [], walls: number[] = [];
  const inside = (x: number, z: number) => x >= 0 && x < width && z >= 0 && z < depth;

  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[], both = false) => {
    const base = positions.length / 3;
    positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) colors.push(col[0], col[1], col[2]);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    if (both) indices.push(base, base + 2, base + 1, base, base + 3, base + 2);
  };
  /** A box between two heights, its foot `wb` and head `wt` half-wide, square about (x, z), turned by `yaw`. */
  const column = (x: number, z: number, y0: number, y1: number, wb: number, wt: number, col: number[], yaw = 0, cap = false) => {
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const corner = (k: number, w: number) => {
      const u = (k === 0 || k === 3 ? -1 : 1) * w, v = (k < 2 ? -1 : 1) * w;
      return [x + u * c - v * s, z + u * s + v * c];
    };
    for (let k = 0; k < 4; k++) {
      const [ax, az] = corner(k, wb), [bx, bz] = corner((k + 1) % 4, wb);
      const [cx, cz] = corner((k + 1) % 4, wt), [dx, dz] = corner(k, wt);
      quad([ax, y0, az], [bx, y0, bz], [cx, y1, cz], [dx, y1, dz], col);
    }
    if (cap) {
      const p = [0, 1, 2, 3].map(k => corner(k, wt));
      quad([p[3][0], y1, p[3][1]], [p[2][0], y1, p[2][1]], [p[1][0], y1, p[1][1]], [p[0][0], y1, p[0][1]], col);
    }
  };
  /** A thin square bar from 3D point a to b, `w` half-wide: a pylon's leg or brace. */
  const strut = (a: number[], b: number[], w: number, col: number[]) => {
    const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], len = Math.hypot(...d);
    if (len < 1e-3) return;
    const u = d.map(v => v / len);
    // Two directions square to the bar.
    const ref = Math.abs(u[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    const cross = (p: number[], q: number[]) => [p[1] * q[2] - p[2] * q[1], p[2] * q[0] - p[0] * q[2], p[0] * q[1] - p[1] * q[0]];
    const n1 = cross(u, ref), l1 = Math.hypot(...n1), e1 = n1.map(v => (v / l1) * w);
    const e2 = cross(u, e1.map(v => v / w)).map(v => v * w);
    const off = [[1, 1], [1, -1], [-1, -1], [-1, 1]].map(([i, j]) => [e1[0] * i + e2[0] * j, e1[1] * i + e2[1] * j, e1[2] * i + e2[2] * j]);
    for (let k = 0; k < 4; k++) {
      const o0 = off[k], o1 = off[(k + 1) % 4];
      quad([a[0] + o0[0], a[1] + o0[1], a[2] + o0[2]], [a[0] + o1[0], a[1] + o1[1], a[2] + o1[2]],
        [b[0] + o1[0], b[1] + o1[1], b[2] + o1[2]], [b[0] + o0[0], b[1] + o0[1], b[2] + o0[2]], col, true);
    }
  };
  /** A steel lattice pylon: four legs leaning in, braced every few metres, a cross-arm and a peak. */
  const pylon = (x: number, z: number, g: number, yaw: number) => {
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const at = (u: number, v: number, y: number) => [x + u * c - v * s, y, z + u * s + v * c];
    const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    const half = (y: number) => PYLON.base + (PYLON.top - PYLON.base) * ((y - g) / PYLON.height);
    for (const [i, j] of corners) strut(at(i * PYLON.base, j * PYLON.base, g - 0.3), at(i * PYLON.top, j * PYLON.top, g + PYLON.height), 0.12, STEEL);
    // Rings and crosses between the legs.
    for (let y = g + 4; y < g + PYLON.height; y += 4.5) {
      const h0 = half(y), y1 = Math.min(g + PYLON.height, y + 4.5), h1 = half(y1);
      for (let k = 0; k < 4; k++) {
        const [i0, j0] = corners[k], [i1, j1] = corners[(k + 1) % 4];
        strut(at(i0 * h0, j0 * h0, y), at(i1 * h0, j1 * h0, y), 0.06, STEEL);
        strut(at(i0 * h0, j0 * h0, y), at(i1 * h1, j1 * h1, y1), 0.05, STEEL);
      }
    }
    // The cross-arm (across the line, along v) and the peak for the earth wire.
    const ya = g + PYLON.height - 0.6;
    strut(at(0, -PYLON.arm * 1.1, ya), at(0, PYLON.arm * 1.1, ya), 0.18, STEEL);
    for (const j of [-1, 1]) strut(at(0, j * PYLON.top, g + PYLON.height), at(0, j * PYLON.arm, ya), 0.08, STEEL);
    strut(at(0, 0, g + PYLON.height), at(0, 0, g + PYLON.height + 2.5), 0.12, STEEL);
  };
  /** A wall along a segment: thickness `t`, from `y0` to `y0 + h` at each end, with its top. */
  const slab = (a: number[], b: number[], t: number, h: number, col: number[]) => {
    const len = Math.hypot(b[0] - a[0], b[2] - a[2]);
    if (len < 1e-3) return;
    const nx = -(b[2] - a[2]) / len * t / 2, nz = (b[0] - a[0]) / len * t / 2;
    const p = (q: number[], side: number, up: number) => [q[0] + nx * side, q[1] + up, q[2] + nz * side];
    quad(p(a, 1, 0), p(b, 1, 0), p(b, 1, h), p(a, 1, h), col);
    quad(p(b, -1, 0), p(a, -1, 0), p(a, -1, h), p(b, -1, h), col);
    quad(p(a, 1, h), p(b, 1, h), p(b, -1, h), p(a, -1, h), col);
    quad(p(a, -1, 0), p(a, 1, 0), p(a, 1, h), p(a, -1, h), col);
    quad(p(b, 1, 0), p(b, -1, 0), p(b, -1, h), p(b, 1, h), col);
  };
  /** A thin wire from a to b sagging `sag` metres at its middle, as a narrow ribbon seen from both sides. */
  const wire = (a: number[], b: number[], sag: number, t0: number, t1: number) => {
    const steps = 8, w = 0.035;
    for (let i = 0; i < steps; i++) {
      const u0 = t0 + (t1 - t0) * (i / steps), u1 = t0 + (t1 - t0) * ((i + 1) / steps);
      const at = (u: number) => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u - sag * 4 * u * (1 - u), a[2] + (b[2] - a[2]) * u];
      const p = at(u0), q = at(u1);
      quad([p[0], p[1] - w, p[2]], [q[0], q[1] - w, q[2]], [q[0], q[1] + w, q[2]], [p[0], p[1] + w, p[2]], WIRE, true);
    }
  };
  /** The part [t0, t1] of segment a-b inside the tile, or undefined (Liang-Barsky). */
  const clip = (ax: number, az: number, bx: number, bz: number): [number, number] | undefined => {
    let t0 = 0, t1 = 1;
    const dx = bx - ax, dz = bz - az;
    for (const [p, q] of [[-dx, ax], [dx, width - ax], [-dz, az], [dz, depth - az]]) {
      if (p === 0) { if (q < 0) return undefined; continue; }
      const r = q / p;
      if (p < 0) { if (r > t1) return undefined; if (r > t0) t0 = r; } else { if (r < t0) return undefined; if (r < t1) t1 = r; }
    }
    return t0 < t1 ? [t0, t1] : undefined;
  };

  if (data) {
    for (let li = 0; li < data.kinds.length; li++) {
      const kind = OSM_LINE_KINDS[data.kinds[li]];
      const start = data.ranges[li * 2], count = data.ranges[li * 2 + 1];
      const px = (k: number) => data.points[(start + k) * 2], pz = (k: number) => data.points[(start + k) * 2 + 1];
      if (kind === 'power' || kind === 'minor_power') {
        const big = kind === 'power';
        const top = big ? PYLON.height : POLE.height, arm = big ? PYLON.arm : POLE.arm;
        // Each pylon turned square to the line through it, its conductors on the arm.
        const yawAt = (k: number) => {
          const a = Math.max(0, k - 1), b = Math.min(count - 1, k + 1);
          return Math.atan2(pz(b) - pz(a), px(b) - px(a));
        };
        const conductors = (k: number) => {
          const yaw = yawAt(k), g = ground(px(k), pz(k));
          const ox = -Math.sin(yaw), oz = Math.cos(yaw);
          const spots = big ? [-arm, 0, arm] : [-arm, arm];
          // Hanging just under the arm; on a pylon the middle one under the arm's middle too.
          return spots.map(o => [px(k) + ox * o, g + top - (big ? 1.2 : 0.3), pz(k) + oz * o]);
        };
        for (let k = 0; k < count; k++) {
          if (!inside(px(k), pz(k))) continue;
          const g = ground(px(k), pz(k)), yaw = yawAt(k);
          if (big) {
            pylon(px(k), pz(k), g, yaw);
            walls.push(...squareWalls(px(k), pz(k), PYLON.base, g - 0.5, g + top));
          } else {
            column(px(k), pz(k), g - 0.3, g + top, POLE.radius, POLE.radius * 0.8, WOOD, yaw, true);
            const ox = -Math.sin(yaw), oz = Math.cos(yaw);
            slab([px(k) - ox * arm, g + top - 0.4, pz(k) - oz * arm], [px(k) + ox * arm, g + top - 0.4, pz(k) + oz * arm], 0.1, 0.1, WOOD);
            walls.push(...squareWalls(px(k), pz(k), POLE.radius + 0.05, g - 0.5, g + top));
          }
        }
        for (let k = 0; k + 1 < count; k++) {
          const part = clip(px(k), pz(k), px(k + 1), pz(k + 1));
          if (!part) continue;
          const span = Math.hypot(px(k + 1) - px(k), pz(k + 1) - pz(k));
          const a = conductors(k), b = conductors(k + 1);
          for (let c = 0; c < a.length; c++) wire(a[c], b[c], span * SAG, part[0], part[1]);
        }
        continue;
      }
      // Barriers, clipped to the tile: on a bridge on its deck, else on the ground.
      const tagged = data.heights[li];
      const flags = data.flags?.[li] ?? 0;
      const metal = (flags & LINE_METAL) !== 0 && kind === 'fence';
      const base = (flags & LINE_RAISED) !== 0 && deck !== undefined
        ? (x: number, z: number) => deck(x, z) ?? ground(x, z) : ground;
      for (let k = 0; k + 1 < count; k++) {
        const part = clip(px(k), pz(k), px(k + 1), pz(k + 1));
        if (!part) continue;
        const at = (t: number) => {
          const x = px(k) + (px(k + 1) - px(k)) * t, z = pz(k) + (pz(k + 1) - pz(k)) * t;
          return [x, base(x, z), z];
        };
        const len = Math.hypot(px(k + 1) - px(k), pz(k + 1) - pz(k)) * (part[1] - part[0]);
        // Long pieces in short steps, so the barrier follows the ground; and
        // open where a way passes through it, half a metre at a time.
        const runs: [number, number][] = [];
        if (ways === undefined || len < 1e-6) runs.push([part[0], part[1]]);
        else {
          const dx = (px(k + 1) - px(k)) / (len / (part[1] - part[0])), dz = (pz(k + 1) - pz(k)) / (len / (part[1] - part[0]));
          const n = Math.max(1, Math.ceil(len / GAP_STEP));
          let from: number | undefined;
          for (let i = 0; i < n; i++) {
            const t0 = part[0] + (part[1] - part[0]) * (i / n), t1 = part[0] + (part[1] - part[0]) * ((i + 1) / n);
            const m = at((t0 + t1) / 2);
            if (ways.crosses(m[0], m[2], dx, dz, m[1])) {
              if (from !== undefined) runs.push([from, t0]);
              from = undefined;
            } else if (from === undefined) from = t0;
          }
          if (from !== undefined) runs.push([from, part[1]]);
        }
        for (const [r0, r1] of runs) {
          const runLen = len * (r1 - r0) / (part[1] - part[0] || 1);
          const steps = Math.max(1, Math.ceil(runLen / 4));
          for (let s = 0; s < steps; s++) {
            const a = at(r0 + (r1 - r0) * (s / steps)), b = at(r0 + (r1 - r0) * ((s + 1) / steps));
            const h = kind === 'fence' ? (Number.isNaN(tagged) ? 1.2 : Math.min(3, tagged))
              : kind === 'wall' ? (Number.isNaN(tagged) ? 1 : Math.min(4, tagged)) : (Number.isNaN(tagged) ? 1.5 : Math.min(4, tagged));
            if (kind === 'wall') slab([a[0], a[1] - 0.2, a[2]], [b[0], b[1] - 0.2, b[2]], 0.45, h + 0.2, STONE);
            else if (kind === 'hedge') slab([a[0], a[1] - 0.2, a[2]], [b[0], b[1] - 0.2, b[2]], 0.9, h + 0.2, HEDGE);
            else if (metal) {
              // A railing: a handrail, a foot rail, and thin balusters between.
              for (const [y, t] of [[h - 0.06, 0.06], [0.08, 0.05]]) {
                quad([a[0], a[1] + y, a[2]], [b[0], b[1] + y, b[2]], [b[0], b[1] + y + t, b[2]], [a[0], a[1] + y + t, a[2]], METAL, true);
              }
              const seg = Math.hypot(b[0] - a[0], b[2] - a[2]);
              const n = Math.max(1, Math.round(seg / BALUSTER));
              const ux = (b[0] - a[0]) / (seg || 1), uz = (b[2] - a[2]) / (seg || 1);
              for (let i = 0; i < n; i++) {
                const t = (i + 0.5) / n;
                const x = a[0] + (b[0] - a[0]) * t, z = a[2] + (b[2] - a[2]) * t, y = a[1] + (b[1] - a[1]) * t;
                quad([x - ux * 0.015, y + 0.08, z - uz * 0.015], [x + ux * 0.015, y + 0.08, z + uz * 0.015],
                  [x + ux * 0.015, y + h - 0.06, z + uz * 0.015], [x - ux * 0.015, y + h - 0.06, z - uz * 0.015], METAL, true);
              }
            } else {
              // Two rails, seen from both sides.
              for (const r of [0.35, 0.9]) {
                const y = (h * r) / 1.2;
                quad([a[0], a[1] + y, a[2]], [b[0], b[1] + y, b[2]], [b[0], b[1] + y + 0.1, b[2]], [a[0], a[1] + y + 0.1, a[2]], FENCE, true);
              }
            }
            walls.push(a[0], a[2], b[0], b[2], Math.min(a[1], b[1]) - 0.3, Math.max(a[1], b[1]) + h);
          }
          if (kind === 'fence') {
            // Posts at even spacing along the piece.
            const posts = Math.max(1, Math.round(runLen / FENCE_POST));
            for (let p = 0; p <= posts; p++) {
              const q = at(r0 + (r1 - r0) * (p / posts));
              const h = Number.isNaN(tagged) ? 1.2 : Math.min(3, tagged);
              // A railing's posts are steel and slim, set into the deck rather than dug in.
              column(q[0], q[2], q[1] - (metal ? 0.05 : 0.3), q[1] + h + 0.05, metal ? 0.035 : 0.05, metal ? 0.035 : 0.05,
                metal ? METAL : FENCE, Math.atan2(pz(k + 1) - pz(k), px(k + 1) - px(k)), true);
            }
          }
        }
      }
    }
  }

  if (masts) {
    for (let i = 0; i < masts.length; i += 3) {
      const x = masts[i], z = masts[i + 1];
      if (!inside(x, z)) continue;
      const tagged = masts[i + 2], h = Number.isNaN(tagged) ? 40 : Math.min(300, Math.max(10, tagged));
      const g = ground(x, z);
      // Red and white bands, wider at the foot; antennas near the top.
      const bands = 6;
      for (let b = 0; b < bands; b++) {
        const y0 = g + (h * b) / bands, y1 = g + (h * (b + 1)) / bands;
        const w0 = 1.6 - 1.2 * (b / bands), w1 = 1.6 - 1.2 * ((b + 1) / bands);
        column(x, z, b === 0 ? g - 0.3 : y0, y1, w0, w1, b % 2 === 0 ? RED : WHITE, 0, b === bands - 1);
      }
      for (let a = 0; a < 3; a++) {
        const yaw = (a * Math.PI * 2) / 3;
        const ax = x + Math.cos(yaw) * 0.9, az = z + Math.sin(yaw) * 0.9;
        column(ax, az, g + h * 0.82, g + h * 0.82 + 2.2, 0.25, 0.25, WHITE, yaw, true);
      }
      walls.push(...squareWalls(x, z, 1.6, g - 0.5, g + h));
    }
  }

  return {
    mesh: { positions: new Float32Array(positions), colors: new Float32Array(colors), indices: new Uint32Array(indices) },
    walls: new Float32Array(walls),
  };
}

/** Four walls round a square foot, half-width `r`. */
function squareWalls(x: number, z: number, r: number, bottom: number, top: number): number[] {
  return [
    x - r, z - r, x + r, z - r, bottom, top,
    x + r, z - r, x + r, z + r, bottom, top,
    x + r, z + r, x - r, z + r, bottom, top,
    x - r, z + r, x - r, z - r, bottom, top,
  ];
}
